import { MenuList, Paper, Popper, styled } from '@mui/material';
import React, { type JSX } from 'react';

import { colorSystem } from '../../../theme';
import { MenuItem } from '../../MenuItem';
import { Text } from '../../Text';
import { TextField } from '../../TextField';
import {
    applyPlainTextChange,
    findMentionQuery,
    insertMention,
    matchesQuery,
    MentionQuery,
    toPlainText,
} from '../utils/mentionMarkup';

const URL_REGEX = /^https?:\/\/[^\s]+$/;

function isUrl(text: string): boolean {
    return URL_REGEX.test(text.trim());
}

interface Props<TUser extends { id: string }> {
    users: TUser[];

    formatMention: (user: TUser) => string;

    formatOrg: (user: TUser) => string;

    /**
     * The comment as it is stored and submitted: plain text in which mentions are encoded as
     * `@[<display>](<id>)`. The input shows the plain text of this value.
     */
    value: string;

    /** Called with the new markup value. */
    onChange: (markup: string) => void;

    placeholder?: string;

    disabled?: boolean;

    inputRef?: React.RefObject<HTMLTextAreaElement | null>;

    onKeyUp?: React.KeyboardEventHandler;

    /** Called when files (e.g. screenshots) are pasted */
    onPasteFiles?: (files: File[]) => void;
}

/**
 * The comment input: a regular design-system text field plus an @-mention picker.
 *
 * The value the component reads and writes is the mention markup (`@[display](id)`), while the text
 * field itself shows and edits the plain text. Edits are translated back onto the markup by
 * `utils/mentionMarkup`, which keeps mentions atomic — editing into one removes it entirely.
 */
export function CommentsInput<TUser extends { id: string }>({
    users,
    formatMention,
    formatOrg,
    value,
    onChange,
    placeholder,
    disabled,
    inputRef,
    onKeyUp,
    onPasteFiles,
}: Props<TUser>): JSX.Element {
    const localRef = React.useRef<HTMLTextAreaElement | null>(null);
    const anchorRef = React.useRef<HTMLDivElement | null>(null);
    const [query, setQuery] = React.useState<MentionQuery | null>(null);
    const [focusedIndex, setFocusedIndex] = React.useState(0);

    const plainText = React.useMemo(() => toPlainText(value), [value]);

    const suggestions = React.useMemo(() => {
        if (query === null) {
            return [];
        }
        return users.filter((user) => matchesQuery(formatMention(user), query.text));
    }, [query, users, formatMention]);

    const isOpen = query !== null && suggestions.length > 0;

    const getTextarea = React.useCallback(() => inputRef?.current ?? localRef.current, [inputRef]);

    // Caret position to restore after a change that rewrote the text, e.g. because a mention was
    // deleted as a whole or inserted. `null` means "leave the caret where the browser put it".
    const pendingCaret = React.useRef<number | null>(null);
    React.useLayoutEffect(() => {
        const caret = pendingCaret.current;
        const textarea = getTextarea();
        if (caret === null || !textarea) {
            return;
        }
        pendingCaret.current = null;
        textarea.setSelectionRange(caret, caret);
    }, [getTextarea, plainText]);

    // Changes that don't come from the text field (the "mention" button appending an `@`, the reply
    // button quoting a comment, a restored draft) move the caret to the end and re-evaluate whether
    // a mention is being typed, so the picker opens for the `@` the mention button just added.
    const lastEmitted = React.useRef(value);
    React.useEffect(() => {
        if (value === lastEmitted.current) {
            return;
        }
        lastEmitted.current = value;
        const textarea = getTextarea();
        const nextPlainText = toPlainText(value);
        pendingCaret.current = nextPlainText.length;
        setFocusedIndex(0);
        setQuery(
            textarea && document.activeElement === textarea
                ? findMentionQuery(nextPlainText, nextPlainText.length)
                : null,
        );
    }, [value, getTextarea]);

    const emit = React.useCallback(
        (change: { markup: string; plainText: string; caret: number }, nextQuery: MentionQuery | null) => {
            lastEmitted.current = change.markup;
            pendingCaret.current = change.caret;
            setQuery(nextQuery);
            setFocusedIndex(0);
            onChange(change.markup);
        },
        [onChange],
    );

    const handleChange = React.useCallback(
        (event: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) => {
            const target = event.target;
            const nextPlainText = target.value;
            const caret = target.selectionStart ?? nextPlainText.length;

            const change = applyPlainTextChange(value, nextPlainText, caret);
            emit(change, findMentionQuery(change.plainText, change.caret));
        },
        [value, emit],
    );

    const selectSuggestion = React.useCallback(
        (user: TUser) => {
            const textarea = getTextarea();
            if (query === null) {
                return;
            }
            const caret = textarea?.selectionStart ?? plainText.length;
            emit(insertMention(value, query, caret, { display: formatMention(user), id: user.id }), null);
            textarea?.focus();
        },
        [emit, formatMention, getTextarea, plainText.length, query, value],
    );

    const handleKeyDown = React.useCallback(
        (event: React.KeyboardEvent<HTMLTextAreaElement | HTMLInputElement>) => {
            if (!isOpen) {
                return;
            }
            switch (event.key) {
                case 'ArrowDown':
                    event.preventDefault();
                    setFocusedIndex((index) => (index + 1) % suggestions.length);
                    return;
                case 'ArrowUp':
                    event.preventDefault();
                    setFocusedIndex((index) => (index - 1 + suggestions.length) % suggestions.length);
                    return;
                case 'Enter':
                case 'Tab': {
                    // Ctrl + Enter submits the comment, that shortcut wins over picking a suggestion.
                    if (event.key === 'Enter' && (event.ctrlKey || event.metaKey)) {
                        setQuery(null);
                        return;
                    }
                    event.preventDefault();
                    selectSuggestion(suggestions[focusedIndex] ?? suggestions[0]);
                    return;
                }
                case 'Escape':
                    // Swallowed so that closing the picker doesn't also close a surrounding drawer.
                    event.preventDefault();
                    event.stopPropagation();
                    setQuery(null);
                    return;
                default:
                    return;
            }
        },
        [focusedIndex, isOpen, selectSuggestion, suggestions],
    );

    const handleSelectionChange = React.useCallback(
        (event: React.SyntheticEvent<HTMLTextAreaElement | HTMLInputElement>) => {
            const target = event.currentTarget;
            const caret = target.selectionStart;
            if (caret === null || target.selectionEnd !== caret) {
                return;
            }
            setQuery((current) => {
                const next = findMentionQuery(target.value, caret);
                // Keep the object identity stable, otherwise every caret move re-filters the users.
                if (current !== null && next !== null && current.start === next.start && current.text === next.text) {
                    return current;
                }
                return next;
            });
        },
        [],
    );

    const handlePaste = React.useCallback(
        (event: React.ClipboardEvent<HTMLTextAreaElement | HTMLInputElement>) => {
            // Check for pasted files (e.g. screenshots)
            const files = Array.from(event.clipboardData.files);
            if (files.length > 0 && onPasteFiles) {
                event.stopPropagation();
                onPasteFiles(files);
                return;
            }

            const clipboardText = event.clipboardData.getData('text');

            // Only handle if clipboard contains a URL
            if (!isUrl(clipboardText)) {
                return;
            }

            const target = event.currentTarget;
            const selectionStart = target.selectionStart ?? 0;
            const selectionEnd = target.selectionEnd ?? 0;

            // Only handle if there's selected text: pasting a URL over a selection turns the
            // selection into the link text.
            if (selectionStart === selectionEnd) {
                return;
            }

            event.preventDefault();
            event.stopPropagation();

            const selectedText = target.value.substring(selectionStart, selectionEnd);
            const markdownLink = `[${selectedText}](${clipboardText.trim()})`;
            const nextPlainText =
                target.value.substring(0, selectionStart) + markdownLink + target.value.substring(selectionEnd);

            emit(applyPlainTextChange(value, nextPlainText, selectionStart + markdownLink.length), null);
        },
        [emit, onPasteFiles, value],
    );

    return (
        <Container ref={anchorRef}>
            <StyledTextField
                size="small"
                multiline={true}
                maxRows={6}
                fullWidth={true}
                spellCheck={false}
                value={plainText}
                placeholder={placeholder}
                disabled={disabled}
                inputRef={inputRef ?? localRef}
                onChange={handleChange}
                onKeyUp={onKeyUp}
                onBlur={() => setQuery(null)}
                slotProps={{
                    // Attached to the textarea itself: the events we care about (caret moves, paste)
                    // are only meaningful there, and MUI types the root handlers for its wrapper div.
                    htmlInput: {
                        'aria-autocomplete': 'list',
                        'aria-expanded': isOpen,
                        role: 'combobox',
                        onKeyDown: handleKeyDown,
                        onSelect: handleSelectionChange,
                        onClick: handleSelectionChange,
                        onPaste: handlePaste,
                    },
                }}
            />
            <SuggestionsPopper
                open={isOpen}
                anchorEl={anchorRef.current}
                placement="top-start"
                modifiers={[{ name: 'offset', options: { offset: [0, 4] } }]}
            >
                <SuggestionsPaper>
                    <MenuList role="listbox" disablePadding={true}>
                        {suggestions.map((user, index) => (
                            <MenuItem
                                key={user.id}
                                role="option"
                                aria-selected={index === focusedIndex}
                                selected={index === focusedIndex}
                                // Keeps the focus (and therefore the caret) in the text field.
                                onMouseDown={(event: React.MouseEvent) => event.preventDefault()}
                                onMouseEnter={() => setFocusedIndex(index)}
                                onClick={() => selectSuggestion(user)}
                                label={
                                    <Text variant="body-small">
                                        {formatMention(user).replace(/^@/, '')}
                                        <br />
                                        <small>{formatOrg(user)}</small>
                                    </Text>
                                }
                            />
                        ))}
                    </MenuList>
                </SuggestionsPaper>
            </SuggestionsPopper>
        </Container>
    );
}

const Container = styled('div')({
    flexGrow: 1,
    minWidth: 0,
});

const StyledTextField = styled(TextField)({
    // The comment input grows with its content instead of being a fixed-height single line.
    '& .MuiOutlinedInput-input': {
        height: 'auto',
    },
});

const SuggestionsPopper = styled(Popper)(({ theme }) => ({
    // The popper is rendered in a portal on `document.body`, outside the drawer the comments live in,
    // so it needs to clear that drawer's own z-index (1200) to be visible at all. `zIndex.modal` is
    // the layer MUI's Autocomplete popper uses, which also clears a surrounding dialog.
    zIndex: theme.zIndex.modal,
}));

const SuggestionsPaper = styled(Paper)({
    maxHeight: 300,
    overflowY: 'auto',
    border: `1px solid ${colorSystem.neutral[2]}`,
    borderRadius: 4,
});
